import React, { ChangeEvent, InputHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';

import { mixinThemingShape } from '../../theme';

import { FormElement } from '../Form';
import { IconDeprecated } from '../deprecated/IconDeprecated';
import { CosmosComponent } from '../../types';

export interface InputProps extends FormElement<CosmosComponent> {
  onInputChange?: (value: string) => void;
  ref?: React.Ref<HTMLInputElement>;
  isError?: boolean;
}

export type InputComponent = InputProps & InputHTMLAttributes<HTMLInputElement>;

const defaultProps: Partial<InputComponent> = {
  isInverted: false,
  type: 'text',
  isError: false,
};

const PlainInput = React.forwardRef(
  (
    { onInputChange, testId, ...props }: InputComponent,
    ref: React.Ref<HTMLInputElement>,
  ) => (
    <PlainInputWrapper
      {...props}
      data-test-id={testId}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        if (onInputChange) {
          onInputChange(event.target.value);
        }
      }}
      ref={ref}
    />
  ),
);

const Input = React.forwardRef(
  (
    {
      className,
      icon,
      isInverted,
      onInputChange,
      testId,
      isError,
      ...props
    }: InputComponent,
    ref: React.Ref<HTMLInputElement>,
  ) => (
    <InputWrapper
      className={className}
      isInverted={isInverted}
      isError={isError}
    >
      {icon && <IconWrapper isInverted name={icon} />}
      <PlainInput
        {...props}
        isInverted={isInverted}
        onInputChange={onInputChange}
        testId={testId}
        ref={ref}
      />
    </InputWrapper>
  ),
);

type PlainInputWrapperProps = {
  isInverted?: boolean;
};

const PlainInputWrapper = styled.input<PlainInputWrapperProps>`
  width: 100%;
  height: 100%;
  border: 0;
  padding: 0;
  outline: none;
  font-size: 14px;
  background: inherit;
  color: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Open Sans', sans-serif;

  &::placeholder {
    color: ${({ theme: { colors } }) => colors?.primerGrey};
  }
  /* Theming Overrides */
  ${({ theme }) => theme?.overrides?.PlainInput}

  ${({ isInverted }) =>
    isInverted &&
    css`
      &::placeholder {
        color: ${({ theme: { colors } }) => colors?.invertedPrimerGrey};
      }
    `}
`;

const IconWrapper = styled(IconDeprecated)`
  display: flex;
  height: 18px;
  width: 18px;
  margin-right: 5px;

  & svg {
    width: 100%;
    margin: auto 0;
  }
`;

type InputWrapperProps = {
  isInverted?: boolean;
  isError?: boolean;
};

const InputWrapper = styled.div<InputWrapperProps>`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  color: ${({ theme: { colors } }) => colors?.textColor};
  background-color: ${({ theme: { colors } }) => colors?.white};
  transition: background-color 0.1s ease-out;
  font-family: 'Open Sans', sans-serif;

  /* Theming Overrides */
  ${({ theme: { colors } }) =>
    mixinThemingShape('input', {
      border: `1px solid ${colors?.borderGray}`,
      borderRadius: '2px',
      padding: '0 8px',
    })}
    
    ${({ theme }) => theme?.overrides?.input}

  ${({ isInverted }) =>
    isInverted &&
    css`
      background-color: ${({ theme: { colors } }) => colors?.invertedDefault};
      color: ${({ theme: { colors } }) => colors?.white};
      border: none;

      &:hover {
        background-color: ${({ theme: { colors } }) => colors?.battleshipGrey};
      }
    `}

    ${({ isError }) =>
      isError &&
      css`
        border: 1px solid ${({ theme: { colors } }) => colors?.red};
      `}
`;

PlainInput.defaultProps = defaultProps;
PlainInput.displayName = 'PlainInput';
Input.defaultProps = defaultProps;
Input.displayName = 'Input';

export { PlainInput, Input };
